/**
 * **La niche de chaîne** — SPEC-MOTEUR §7.4, ⟦A-4 décision 12 / arbitrage C⟧.
 *
 * Mathieu a tranché la forme : *« Implicite : X vidéos consécutives majoritairement dans un
 * même thème = niche reconnue automatiquement (bandeau « chaîne de vlog »), prime de
 * fidélité/rente tant que la cohérence tient. […] Le touche-à-tout reste viable, sans la
 * prime. »* Ce module pose le X et les deux primes ; il ne décide rien d'autre.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔑 **Pourquoi un compteur neuf, et pas `consecutive_same_theme`**
 *
 * SPEC-MOTEUR §7.4 le signalait lui-même : *« le thème épuisé (§2.4) le remet à zéro tous les
 * 3 — la niche devra compter autrement »*. C'est exact, et c'est plus profond qu'un détail
 * d'implémentation : `consecutive_same_theme` compte des vidéos **consécutives**, or trois
 * vidéos consécutives sur un thème le brûlent 14 jours (`H_THEME_EPUISE`). Adossée à ce
 * compteur, la niche serait une mécanique qui s'auto-interdit à l'instant où elle se déclenche.
 *
 * D'où la lecture retenue, qui est celle du mot **« majoritairement »** d'⟦A-4 C⟧ : une
 * **fenêtre glissante** des `NICHE_FENETRE` dernières vidéos publiées, et une niche reconnue
 * dès qu'un thème y occupe `NICHE_SEUIL` places. Les deux règles cessent alors de se battre et
 * se mettent à dessiner un rythme :
 *
 * ```
 *   A A A  → la 3ᵉ donne la prime… et brûle le thème 14 jours (§2.4)
 *   A B A B A → 3 A sur 5, niche reconnue, `consecutive_same_theme` ne dépasse jamais 2
 * ```
 *
 * **Alterner dans sa niche est donc meilleur que s'y acharner** — la niche récompense une
 * ligne éditoriale, pas une répétition. Ce n'était pas cherché : c'est tombé du croisement des
 * deux règles, et c'est la raison pour laquelle la fenêtre vaut 5 et le seuil 3.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️ **Cette mécanique N'A PAS d'état neutre, et c'est assumé** ⟦A-10⟧.
 *
 * Toute partie enchaîne des sujets, donc toute partie finit par former une majorité de thème :
 * il n'existe aucune borne (`subs_min`, un flag, un interrupteur de contenu) derrière laquelle
 * la ranger sans la vider de son sens. La rupture est **mesurée et publiée** dans l'avenant
 * plutôt que subie ; la campagne d'après devient la nouvelle référence.
 *
 * Deux garde-fous limitent quand même sa portée, et ils sont volontaires :
 *  1. **La prime saute sur une vidéo `nulle`** (`primeNicheDue`). Une chaîne cohérente
 *     fidélise — à condition que les vidéos tiennent. Effet de bord recherché : un joueur à
 *     l'exécution faible flope plus, donc touche la prime moins souvent, donc la corrélation
 *     ⟦A-22⟧ n'est pas aplatie par un bonus que tout le monde ramasse.
 *  2. **Aucun malus**, jamais. Le touche-à-tout perd la prime, il ne paie rien ⟦A-4 C⟧.
 */
// ─────────────────────────────────────────────────────────────────────────────
// Les valeurs — le « X ✎ » d'⟦A-4 C⟧, posé ici
// ─────────────────────────────────────────────────────────────────────────────
/** Taille de la fenêtre glissante : les **5 dernières vidéos publiées**. */
export const NICHE_FENETRE = 5;
/**
 * Combien de ces 5 doivent partager un thème pour que la niche soit reconnue : **3**.
 *
 * C'est la majorité stricte de 5, et c'est aussi le plus petit seuil qui reste franchissable
 * **sans** déclencher le thème épuisé (§2.4) — voir l'en-tête. Un seuil de 4 rendrait la niche
 * incompatible avec sa propre contrainte voisine ; un seuil de 2 la donnerait par accident.
 */
export const NICHE_SEUIL = 3;
/**
 * **Prime de Fidélité** — ce que rapporte une vidéo publiée *dans* sa niche reconnue.
 *
 * Ordre de grandeur volontairement aligné sur le bonus de bande (`+1` pour une publication
 * Lumière, `publish.ts` §5.1) et non sur celui de la longue réussie (`+3`) : la cohérence
 * éditoriale est un pli qu'on prend, pas un coup d'éclat.
 */
export const NICHE_FIDELITE = 1;
/**
 * **Prime de rente** — le taux hebdomadaire d'une evergreen publiée dans sa niche, ×1,15.
 *
 * Figée à la publication comme le ×1,2 des séries (`tauxRenteFige`) : une prime qu'on
 * recalculerait rétroactivement ferait varier le passé d'une chaîne à chaque changement de
 * ligne éditoriale. Cumulable avec la série — ce sont deux gestes différents (publier
 * cohérent, ranger sa bibliothèque), et le plafond `RENTE_PLAFOND` borne le total.
 *
 * ⚠️ **Elle est presque invisible en campagne** et il faut le dire : le harnais ne produit que
 * **9 evergreens sur 100 parties**. La prime de rente est écrite pour le joueur humain qui
 * construit un fonds de catalogue, pas pour la mesure — qui, elle, ne verra que la Fidélité.
 */
export const NICHE_RENTE_MULT = 1.15;
// ─────────────────────────────────────────────────────────────────────────────
// Les libellés — le bandeau « chaîne de vlog » d'⟦A-4 C⟧
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Le bandeau, thème par thème. **Phrase complète**, pas un mot à recoller : « chaîne
 * d'enquête » et « chaîne du terroir » ne se fabriquent pas avec le même article, et un
 * moteur qui élide est un moteur qui parle mal français un jour sur deux.
 *
 * ⚠️ **Table de LIBELLÉS, pas de règle** — la distinction vaut d'être posée, parce que
 * CLAUDE.md interdit d'écrire une règle de contenu en dur dans le moteur. `ThemeId` est un
 * **enum fermé de schéma** (`content/schema.ts`), pas une collection de contenu : lui donner
 * un fichier `content/themes.json` aurait ajouté un 13ᵉ chargeur à tenir de quatre côtés
 * (serveur, bac à sable client, harnais, Zod) pour douze chaînes de caractères qui ne peuvent
 * pas varier sans que l'enum varie d'abord. Même catégorie que la table `FINS` du Palmarès.
 *
 * L'exhaustivité n'est pas laissée à la vigilance : un test de `content/coherence.test.ts`
 * vérifie que **chaque option de `ThemeId` a son libellé**. Ajouter un thème sans son bandeau
 * fait tomber la CI.
 */
export const NICHE_LIBELLES = {
    internet_culture: "chaîne de culture web",
    defi: "chaîne de défis",
    voyage: "chaîne de voyage",
    tech_ia: "chaîne tech",
    gaming: "chaîne de gaming",
    wtf_tests: "chaîne de tests absurdes",
    drama_reaction: "chaîne de réaction",
    caritatif: "chaîne solidaire",
    lifestyle: "chaîne lifestyle",
    tutoriel_evergreen: "chaîne de tutos",
    enquete: "chaîne d'enquête",
    village: "chaîne du terroir",
};
/** Le bandeau d'un thème, ou le thème brut si personne ne lui a écrit de libellé. */
export function libelleNiche(theme) {
    return NICHE_LIBELLES[theme] ?? `chaîne de ${theme}`;
}
/**
 * La fenêtre glissante, tolérante à l'absence. **`undefined` ⇔ `[]` ⇔ aucune niche** : un état
 * déjà sérialisé (avant cet avenant) reste valide sans migration.
 */
export function fenetreThemes(state) {
    return state.counters.derniers_themes ?? [];
}
/**
 * Le thème majoritaire d'une fenêtre, ou `null`.
 *
 * En cas d'égalité — possible seulement à `NICHE_SEUIL` exactement, donc jamais sur une
 * fenêtre de 5 avec un seuil de 3 — on rend le premier dans l'ordre alphabétique : un
 * départage stable vaut mieux qu'un ordre d'itération de `Map`, même sur un cas injoignable
 * aujourd'hui (le jour où la fenêtre ou le seuil bougent, la règle est déjà écrite).
 */
export function nicheDeLaFenetre(fenetre) {
    const comptes = new Map();
    for (const t of fenetre)
        comptes.set(t, (comptes.get(t) ?? 0) + 1);
    let meilleur = null;
    let meilleurCompte = 0;
    for (const [theme, n] of [...comptes.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1))) {
        if (n > meilleurCompte) {
            meilleur = theme;
            meilleurCompte = n;
        }
    }
    return meilleurCompte >= NICHE_SEUIL ? meilleur : null;
}
/** La niche reconnue **aujourd'hui**, ou `null` (touche-à-tout, ou début de partie). */
export function nicheReconnue(state) {
    return nicheDeLaFenetre(fenetreThemes(state));
}
/**
 * La niche **telle qu'elle sera** une fois `theme` publié — sans rien muter.
 *
 * C'est cette lecture-là que `publish.ts` utilise pour les deux primes : la vidéo qu'on est en
 * train de publier compte dans sa propre fenêtre. Sans ça, la 3ᵉ vidéo d'un thème créerait la
 * niche sans en profiter, et le joueur verrait le bandeau apparaître **après** le tour où il
 * l'a mérité.
 */
export function nicheApres(state, theme) {
    return nicheDeLaFenetre(fenetreAvec(fenetreThemes(state), theme));
}
/** La fenêtre après ajout d'un thème, tronquée à `NICHE_FENETRE`. Pure. */
export function fenetreAvec(fenetre, theme) {
    return [...fenetre, theme].slice(-NICHE_FENETRE);
}
/**
 * La prime est-elle due pour cette publication ?
 *
 * Trois conditions, toutes nécessaires : une niche reconnue *après* cette vidéo, cette vidéo
 * **dans** ce thème, et une vidéo qui n'est pas une `nulle`. Voir l'en-tête pour le pourquoi
 * de la troisième.
 */
export function primeNicheDue(state, theme, classe) {
    if (classe === "nulle")
        return false;
    return nicheApres(state, theme) === theme;
}
/**
 * Inscrit le thème d'une vidéo publiée dans la fenêtre. **MUTE** `state.counters`, comme tout
 * le reste du pas 8 de `publier`. Rend la transition, pour que l'appelant écrive la ligne.
 */
export function enregistrerThemePublie(state, theme) {
    const avant = nicheDeLaFenetre(state.counters.derniers_themes ?? []);
    const fenetre = [...fenetreAvec(state.counters.derniers_themes ?? [], theme)];
    state.counters.derniers_themes = fenetre;
    const apres = nicheDeLaFenetre(fenetre);
    return {
        avant,
        apres,
        gagnee: apres !== null && apres !== avant,
        perdue: avant !== null && apres === null,
    };
}
/**
 * La ligne de journal d'une transition, ou `null` s'il n'y a rien à dire.
 *
 * ⟦règle de composition de `client/src/game/journal.ts`⟧ : *« le `log` est la trame, et on
 * n'ajoute un `EngineEvent` que lorsqu'il porte un fait qu'aucune ligne de log ne raconte »*.
 * La niche se raconte entièrement en une phrase — donc une ligne de log, **et aucun
 * `EngineEvent` neuf**. C'est aussi la parade à la rupture n° 2 de `docs/AUDIT-MAILLONS.md` :
 * un `kind:` que le `switch` du client ne connaît pas est un avertissement que personne ne
 * lit. Une ligne de log, elle, est affichée par construction (`journal.ts:276`).
 */
export function ligneNiche(c) {
    if (c.gagnee) {
        return `Le public t'a rangé quelque part : te voilà une ${libelleNiche(c.apres)}. Tant que tu tiens la ligne, ils restent.`;
    }
    if (c.perdue) {
        return `Tu pars dans tous les sens — plus personne ne sait ce que fait ta chaîne. La prime de cohérence tombe.`;
    }
    return null;
}
//# sourceMappingURL=niche.js.map